import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { doseVisibilityState } from '~/state/atoms/dose/doseAtoms';
import {
    showDoseStatisticsTableState,
    showDVHViewComponentState,
} from '~/state/atoms/viewerAtoms';

/**
 * Applies the workflow step's dose display policy to the display atoms.
 * While a dose is selected, the dose overlay is shown by default on the
 * setup, dose and export steps and hidden by default on every other step.
 * The default is written when the selected dose changes and when the step
 * enters or leaves setup, dose or export. Between those moments the
 * visibility is the user's: a dose shown on one of the other steps stays
 * shown while the user moves among those steps. The DVH chart and dose
 * statistics are offered on the dose step only. The atoms are the display
 * truth; the drawn Environments follow them through the display bridge.
 * Mount once, in a component that stays rendered for as long as the viewer
 * does.
 */
export function useDoseStepDisplayPolicy(): void {
    const currentDose = useCurrentDose();
    const { currentStep } = useWorkflowSteps();
    const isOnSetupStep = currentStep?.id === 'setup';
    const isOnDoseStep = currentStep?.id === 'predictive-planning';
    const isOnExportStep = currentStep?.id === 'export';
    const setDoseVisibility = useSetRecoilState(doseVisibilityState);
    const setShowDVHViewComponent = useSetRecoilState(
        showDVHViewComponentState
    );
    const setShowDoseStatisticsTable = useSetRecoilState(
        showDoseStatisticsTableState
    );

    useEffect(() => {
        const dvhAvailable = !!currentDose;
        const showDose =
            dvhAvailable && (isOnSetupStep || isOnDoseStep || isOnExportStep);
        setDoseVisibility(showDose);

        // DVH & statistics panels: null = disabled, false = hidden, true = shown
        // Only show DVH panels on the dose step, not on the export step
        const showDvhPanels = dvhAvailable && isOnDoseStep;
        if (!dvhAvailable) {
            setShowDVHViewComponent(null);
            setShowDoseStatisticsTable(null);
        } else {
            setShowDVHViewComponent(showDvhPanels);
            setShowDoseStatisticsTable(showDvhPanels);
        }
    }, [
        currentDose,
        isOnSetupStep,
        isOnDoseStep,
        isOnExportStep,
        setDoseVisibility,
        setShowDVHViewComponent,
        setShowDoseStatisticsTable,
    ]);
}
